import { useMemo } from 'react';

import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { fNumber } from 'src/utils/format/format-number';
import { lastColumns, toPivotTable } from 'src/utils/pivot/pivot-shape';

import { findMetric } from 'src/constants/pivot-fields';
import { findChartKind } from 'src/constants/pivot-charts';

import { HubSection } from 'src/components/cmui/hub/hub-section';
import { HubTagCloud } from 'src/components/cmui/hub/hub-tag-cloud';
import { AnalyticsChart } from 'src/components/cmui/chart/pivot-chart';
import { HubEntityCard } from 'src/components/cmui/hub/hub-entity-card';

import { weekGrowthLabel } from 'src/sections/brand-hub/utils/brand-hub-growth';

import { HomeSceneFrame } from './home-scene-frame';
import { BRAND_HEAD, BRAND_NAME, BRAND_TREND, BRAND_CO_MENTIONS } from '../constants/home-scene-samples';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

const COUNT_METRIC = findMetric('count')!;

/** 폰의 추이 그림이 세우는 주 수 — 좁은 폭에 열세 칸이면 날짜가 겹친다 */
const PHONE_WEEKS = 5;

/** 머리 카드의 숫자 넷 — 폰은 앞의 둘(언급)만 세운다(장면이 한 화면에 들도록) */
const BRAND_STATS = [
  { label: '최근 7일 언급', value: fNumber(BRAND_HEAD.recent7d), hint: `지난주 대비 ${weekGrowthLabel(BRAND_HEAD.recent7d, BRAND_HEAD.prev7d)}` },
  { label: '최근 13주 언급', value: fNumber(BRAND_HEAD.total), hint: `${BRAND_HEAD.topChannel}에서 가장 많이` },
  { label: '집행 중 광고', value: fNumber(BRAND_HEAD.adTotal), hint: '메타 광고 기준' },
  { label: '판매몰 상품', value: fNumber(BRAND_HEAD.productTotal), hint: '랭킹에 오른 수' },
];

// ----------------------------------------------------------------------

/**
 * 장면 2 · 브랜드 허브 — 브랜드 하나의 머리 카드(최근 7일 · 지난주 대비) · 13주 언급 추이 · 함께 언급된 브랜드.
 *
 * 🔑 셋 다 브랜드 허브 실화면의 부품이다(`HubEntityCard` · `AnalyticsChart` · `HubTagCloud`) — 값만 예시다.
 * 🔑 「지난주 대비」는 실화면과 같은 셈(`weekGrowthLabel`)으로 적는다.
 */
export function HomeSceneBrand({ scene }: { scene: HomeScene }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const trend = useMemo(() => toPivotTable(BRAND_TREND), []);
  const recent = useMemo(() => lastColumns(trend, PHONE_WEEKS), [trend]);

  return (
    <HomeSceneFrame scene={scene} tinted>
      <Stack spacing={1.5}>
        <HubEntityCard
          initial={BRAND_NAME.slice(0, 1)}
          logo
          title={BRAND_NAME}
          meta="최근 13주 · 이 브랜드가 언급된 글 기준"
          stats={phone ? BRAND_STATS.slice(0, 2) : BRAND_STATS}
        />
        <HubSection title="언급 추이" hint={`최근 ${phone ? PHONE_WEEKS : trend.columns.length}주 · 주별 언급 수`} dense>
          <AnalyticsChart table={phone ? recent : trend} metric={COUNT_METRIC} kind={findChartKind('area')} height={phone ? 150 : 200} legend={false} chartId="home-brand-trend" />
        </HubSection>
        <HubSection title="함께 언급된 브랜드" hint="같은 글에 같이 나온 브랜드" dense>
          <HubTagCloud rows={BRAND_CO_MENTIONS} />
        </HubSection>
      </Stack>
    </HomeSceneFrame>
  );
}
